import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl } from "@angular/forms";
import { debounceTime, switchMap, startWith, map } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { SummaryOptionItem } from 'src/app/model/optionItem';

export class SelectFilterInfo {
  source: string;
  values: any[];
  active: boolean;

  constructor(source: string, values: any[]) {
    this.source = source;
    this.values = values;
  }
}
export class ValuesColor {
  field: string;
  color: string;

  constructor(field: string, color: string) {
    this.field = field;
    this.color = color;
  }
}
class ViewModel {
  items: SummaryOptionItem[];
}

@Component({
    selector: 'app-select-filter-option',
    templateUrl: './select-filter-option.component.html',
    styleUrls: ['./select-filter-option.component.css'],
    standalone: false
})
export class SelectFilterOptionComponent implements OnInit, OnChanges {

  @Input() source: string;
  @Input() sources: any[];
  @Input() values: any[];
  @Input() emptyColumns: any[];
  @Input() selectedColumns: any[];
  @Input() valuesColor: ValuesColor[]=[];
  @Output() applyFilter = new EventEmitter<SelectFilterInfo>();
  @Output() clearFilter = new EventEmitter<string>();
  @Output() closeFilter = new EventEmitter();
  @Output() changeSource = new EventEmitter<string>();

  viewModel = new ViewModel();
  search = new UntypedFormControl();
  filteredOptions!: Observable<SummaryOptionItem[]>;
  
  constructor() { }

  @ViewChild('resizeContainer') resizeContainer: ElementRef;
  height: number = 330; // Default height
  width: number = 220;
  private resizeObserver: ResizeObserver;

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        this.height = Math.round(entry.contentRect.height);
        this.width = Math.round(entry.contentRect.width);
      }
    });
    if (this.resizeContainer) {
      this.resizeObserver.observe(this.resizeContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
  
  ngOnInit(): void {
     this.refreshValues();
  }
  
  private async refreshValues(): Promise<void> {
    if (this.values) {
      await this.clearAll();
      this.viewModel.items = [];
      if(this.values.length!=0){
        this.viewModel.items.push(new SummaryOptionItem(undefined, '(Select All)', true, false));
      }
      const items = this.values.sort((a, b) => b > a ? -1 : 1);
      // get Unique values only
      const uniqueItems: any[] = Array.from( new Set(items));

      uniqueItems.forEach( i => {
        var color="";
        const valuecolor=this.valuesColor.find(vc=>vc.field===i);
        if(valuecolor){
          color=valuecolor.color;
        }
        const emptycol=this.emptyColumns.find(ec=>ec===i);
        if(emptycol){
          this.viewModel.items.push( new SummaryOptionItem(i, this.getFilterDisplayText(i)+" (No Data)", false,true));
        }else{
          this.viewModel.items.push( new SummaryOptionItem(i, this.getFilterDisplayText(i), true, false,color));
        }
      });   
      
      this.search.setValue("");
      setTimeout(() => {
        this.filteredOptions = this.search.valueChanges.pipe(
          startWith(''),
          map(value => this._filter(value || ''))
        );
      }, 100);
    }
  }

  async clearAll() {
    if(this.viewModel.items){
      this.viewModel.items.forEach(item => item.isSelected = false);
    }
  }
  private getFilterDisplayText(data: any): string {
    if (typeof data === 'string') {
      return data.length === 0 ? '(Blanks)' : data;
    }
    else {
      return String(data);
    }
  }

  ngOnChanges(changes: any): void {
    if (changes.values?.currentValue && changes.values?.currentValue !== changes.values?.previousValue) {
      this.refreshValues();
    }
  }

  onFilterItemSelected(event: any): void {
    if (event.options[0].value === undefined) {
      this.viewModel.items.filter(fitem=>fitem.isDisabled===false).forEach( i => {
        i.isSelected = event.options[0].selected;
      });
    } else {
      // automatically deselect 'All' if we chose one of the other values
      this.viewModel.items[0].isSelected = false;
    }
  }

  onApplyFilter(): void {
    const selectedValues = this.viewModel.items
      .filter(i => i.isSelected === true)
      .map(o => o.value);

    const filterInfo = new SelectFilterInfo(this.source, selectedValues);
    filterInfo.active = true;
    this.applyFilter.emit(filterInfo);
  }

  onClearFilter(): void {
    this.viewModel.items.filter(fitem=>fitem.isDisabled===false).forEach( i => {
      if(this.selectedColumns.includes(i.value)){
        i.isSelected = true;
      }else{
        i.isSelected = false;
      }
    });
    this.clearFilter.emit(this.source);
  }

  onCloseFilter(): void {
    this.closeFilter.emit();
  }
  
  onChangeSource(){
    this.viewModel.items = [...new Set([])];
    this.search.setValue("");
    this.changeSource.emit(this.source);
  }
  // Private method to filter options based on input value
  private _filter(value: string): SummaryOptionItem[] {
    const filterValue = value.toLowerCase();
    if (filterValue==""){ 
      if (this.viewModel.items){ 
        this.viewModel.items.filter(fitem=>fitem.isDisabled===false).forEach(item=>{
          item.isSelected=true;
        })

        if(this.selectedColumns.length>0){
          this.viewModel.items.filter(itm=>itm.isDisabled===false).forEach(item=>{
            var isSelected=this.selectedColumns.find(sc=>sc==item.value);
            if(isSelected){
              item.isSelected=true;
            }else{
              item.isSelected=false;
            }
          });
        }
        return this.viewModel.items;
      }
    }else{
      if (this.viewModel.items){
        this.viewModel.items.filter(fitem=>fitem.isDisabled===false).forEach(item=>{
          item.isSelected=false;
        })
        this.viewModel.items.filter(x => x.text.toLowerCase().indexOf(filterValue) >= 0).filter(fitem=>fitem.isDisabled===false).forEach(itm=>{itm.isSelected=true;}) 
        this.viewModel.items[0].isSelected = false;
        return this.viewModel.items.filter(x => x.text.toLowerCase().indexOf(filterValue) >= 0);
      }
    }
    return [];
  }
}

// $search = this.search.valueChanges.pipe(
//   startWith(null),
//   debounceTime(200),
//   switchMap((res: string) => {
    
//     if (!res){ 
//       if (this.viewModel.items){ 
//         this.viewModel.items.filter(fitem=>fitem.isDisabled===false).forEach(item=>{
//           item.isSelected=true;
//         })

//         if(this.selectedColumns.length==0){
//           this.viewModel.items.filter(fitem=>fitem.isDisabled===false).forEach(item=>{
//             item.isSelected=true;
//           })
//         }else{
//           this.viewModel.items.filter(itm=>itm.isDisabled===false).forEach(item=>{
//             var isSelected=this.selectedColumns.find(sc=>sc==item.value);
//             if(isSelected){
//               item.isSelected=true;
//             }else{
//               item.isSelected=false;
//             }
//           });
//         }

//         return of(this.viewModel.items);
//       }
//     }
//     if (this.viewModel.items){
//       this.viewModel.items.filter(fitem=>fitem.isDisabled===false).forEach(item=>{
//         item.isSelected=false;
//       })
//       this.viewModel.items.filter(x => x.text.toLowerCase().indexOf(res.toLowerCase()) >= 0).filter(fitem=>fitem.isDisabled===false).forEach(itm=>{itm.isSelected=true;}) 
//       this.viewModel.items[0].isSelected = false;
//       return of(
//         this.viewModel.items.filter(x => x.text.toLowerCase().indexOf(res.toLowerCase()) >= 0) 
//       );
//     }
//     return of([]);
//   })
// );
